import {
  APP_STORE,
  MAPS_STORE,
  deleteRecord,
  getAllRecords,
  getRecord,
  putRecord,
} from "@/src/core/persistence/db";
import {
  CURRENT_SCHEMA_VERSION,
  type MapSummary,
  type PersistedMap,
  type PersistedPreferences,
  type StoredRecord,
  migrateRecord,
  summarise,
} from "@/src/core/persistence/schema";
import { t } from "@/src/i18n";
import type { MapDocument, Viewport } from "@/src/types/map";

const LAST_OPENED_KEY = "lastOpenedMapId";
const PREFERENCES_KEY = "preferences";

interface AppRecord<T> {
  key: string;
  value: T;
}

/** Ids are timestamp-prefixed so the natural sort is roughly creation order. */
export const newMapId = (): string =>
  `map_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const isPersistedMap = (record: StoredRecord): record is StoredRecord & PersistedMap =>
  typeof record.id === "string" &&
  typeof record.document === "object" && record.document !== null &&
  typeof record.viewport === "object" && record.viewport !== null;

/** Reads one map, walking it up to the current schema version on the way out. */
export const getMap = async (id: string): Promise<PersistedMap | null> => {
  const stored = await getRecord<StoredRecord>(MAPS_STORE, id);
  if (!stored) return null;
  const migrated = migrateRecord(stored);
  if (!isPersistedMap(migrated)) {
    throw new Error(`Stored map ${id} is missing required fields.`);
  }
  return migrated;
};

/**
 * Lists every saved map, newest first. A single unreadable record is skipped
 * rather than taking down the whole library.
 */
export const listMaps = async (): Promise<MapSummary[]> => {
  const stored = await getAllRecords<StoredRecord>(MAPS_STORE);
  const summaries: MapSummary[] = [];
  for (const record of stored) {
    try {
      const migrated = migrateRecord(record);
      if (isPersistedMap(migrated)) summaries.push(summarise(migrated));
    } catch (error) {
      console.warn("Skipping unreadable map record", record?.id, error);
    }
  }
  return summaries.sort((a, b) => b.updatedAt - a.updatedAt);
};

export const saveMap = async (record: PersistedMap): Promise<PersistedMap> => {
  const stamped: PersistedMap = {
    ...record,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    updatedAt: Date.now(),
  };
  await putRecord(MAPS_STORE, stamped);
  return stamped;
};

export const createMap = async (
  document: MapDocument,
  viewport: Viewport,
  nextRoomNumber: number,
): Promise<PersistedMap> => {
  const now = Date.now();
  const record: PersistedMap = {
    id: newMapId(),
    schemaVersion: CURRENT_SCHEMA_VERSION,
    createdAt: now,
    updatedAt: now,
    document,
    viewport,
    nextRoomNumber,
  };
  await putRecord(MAPS_STORE, record);
  return record;
};

export const renameMap = async (id: string, title: string): Promise<PersistedMap | null> => {
  const existing = await getMap(id);
  if (!existing) return null;
  return saveMap({
    ...existing,
    document: {
      ...existing.document,
      meta: { ...existing.document.meta, title: title.slice(0, 80) },
    },
  });
};

export const duplicateMap = async (id: string): Promise<PersistedMap | null> => {
  const source = await getMap(id);
  if (!source) return null;
  const now = Date.now();
  // structuredClone keeps the copy from sharing geometry arrays with the original.
  const copy: PersistedMap = {
    ...structuredClone(source),
    id: newMapId(),
    createdAt: now,
    updatedAt: now,
  };
  copy.document.meta.title = t("doc.copySuffix", { title: source.document.meta.title }).slice(0, 80);
  await putRecord(MAPS_STORE, copy);
  return copy;
};

export const deleteMap = async (id: string): Promise<void> => {
  await deleteRecord(MAPS_STORE, id);
  if (await getLastOpenedMapId() === id) {
    await deleteRecord(APP_STORE, LAST_OPENED_KEY);
  }
};

export const countMaps = async (): Promise<number> =>
  (await getAllRecords<StoredRecord>(MAPS_STORE)).length;

export const getLastOpenedMapId = async (): Promise<string | null> => {
  const record = await getRecord<AppRecord<string>>(APP_STORE, LAST_OPENED_KEY);
  return record?.value ?? null;
};

export const setLastOpenedMapId = (id: string): Promise<void> =>
  putRecord(APP_STORE, { key: LAST_OPENED_KEY, value: id });

export const getPreferences = async (): Promise<Partial<PersistedPreferences> | null> => {
  const record = await getRecord<AppRecord<Partial<PersistedPreferences>>>(APP_STORE, PREFERENCES_KEY);
  return record?.value ?? null;
};

export const setPreferences = (value: PersistedPreferences): Promise<void> =>
  putRecord(APP_STORE, { key: PREFERENCES_KEY, value });

export const getAppFlag = async (key: string): Promise<boolean> => {
  const record = await getRecord<AppRecord<boolean>>(APP_STORE, key);
  return record?.value === true;
};

export const setAppFlag = (key: string, value: boolean): Promise<void> =>
  putRecord(APP_STORE, { key, value });
